import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AnalyticsService, EventParameters } from './analytics.service';
import { AttributionService } from './attribution.service';
import { LocalStorageKey } from '../models/storageKey';

export interface FunnelStep {
  name: string;
  // 1-based position in the active signup funnel; 0 for auxiliary routes
  // that are tracked but hold no funnel position.
  number: number;
}

// Single source of truth for the funnel. Step names are kept identical to the
// values components used to pass to trackSignupStep so historical GA data
// stays comparable.
export const FUNNEL_STEPS: { [route: string]: FunnelStep } = {
  login: { name: 'login', number: 1 },
  venueType: { name: 'venue_type', number: 2 },
  choosePlan: { name: 'plan_selection', number: 3 },
  review: { name: 'review', number: 4 },
  confirmation: { name: 'confirmation', number: 5 }
};

@Injectable({
  providedIn: 'root'
})
export class FunnelTrackingService {
  private currentStep: FunnelStep | null = null;
  private abandonmentReported = false;

  constructor(
    private router: Router,
    private analytics: AnalyticsService,
    // Constructed here so UTM capture runs at bootstrap, before the router's
    // initial navigation drops the query string.
    private attribution: AttributionService
  ) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.onNavigation(event.urlAfterRedirects));

    // pagehide fires on tab close, refresh, and external navigation, and is
    // the last reliable moment to emit an event (GA dispatches via beacon).
    window.addEventListener('pagehide', () => this.reportAbandonment());
    // If the page is restored from bfcache the user is back — allow a future
    // abandonment to be reported again.
    window.addEventListener('pageshow', () => (this.abandonmentReported = false));
  }

  /**
   * Called by step components at their single genuine success point.
   * Step identity, ordering, and timing are filled in from the route map so
   * components cannot drift out of sync.
   */
  trackStepCompleted(routeOrStepName: string, parameters?: EventParameters): void {
    const step = this.resolveStep(routeOrStepName);
    if (!step) {
      console.warn(`FunnelTracking: unknown step '${routeOrStepName}'`);
      return;
    }

    this.analytics.trackEvent('funnel_step_completed', {
      step_name: step.name,
      step_number: step.number,
      in_funnel: step.number > 0,
      time_on_step_ms: this.analytics.getTimeOnPage(step.name),
      total_elapsed_ms: this.getFunnelElapsedMs(),
      ...this.attribution.getAttribution(),
      ...parameters
    });
  }

  /**
   * Called when the signup irreversibly succeeds (venue created). Suppresses
   * abandonment reporting for the rest of the session.
   */
  markFunnelCompleted(): void {
    localStorage.setItem(LocalStorageKey.FUNNEL_COMPLETED, 'true');
  }

  /** Milliseconds since the user first entered the funnel, across sessions. */
  getFunnelElapsedMs(): number {
    const start = Number(localStorage.getItem(LocalStorageKey.FUNNEL_START_TIME));
    return start ? Date.now() - start : 0;
  }

  private onNavigation(url: string): void {
    const route = url.split('?')[0].split('#')[0].replace(/^\//, '');
    const step = FUNNEL_STEPS[route];
    if (!step) return;

    this.ensureFunnelStarted(step);
    this.analytics.startPageTimer(step.name);

    const furthest = this.getFurthestStep();
    const previous = this.currentStep;
    this.currentStep = step;

    if (step.number > 0 && previous && previous.number > step.number) {
      this.analytics.trackEvent('funnel_step_back', {
        from_step: previous.name,
        from_step_number: previous.number,
        step_name: step.name,
        step_number: step.number
      });
    }

    this.analytics.trackEvent('funnel_step_viewed', {
      step_name: step.name,
      step_number: step.number,
      in_funnel: step.number > 0,
      is_revisit: step.number > 0 && step.number <= furthest,
      furthest_step_number: Math.max(furthest, step.number),
      total_elapsed_ms: this.getFunnelElapsedMs()
    });

    if (step.number > furthest) {
      localStorage.setItem(LocalStorageKey.FUNNEL_FURTHEST_STEP, String(step.number));
    }

    if (step.name === 'confirmation') {
      this.markFunnelCompleted();
    }
  }

  private ensureFunnelStarted(entryStep: FunnelStep): void {
    if (localStorage.getItem(LocalStorageKey.FUNNEL_START_TIME)) return;

    localStorage.setItem(LocalStorageKey.FUNNEL_START_TIME, String(Date.now()));
    this.analytics.trackEvent('signup_started', {
      entry_step: entryStep.name,
      referrer: document.referrer || 'direct',
      ...this.attribution.getAttribution()
    });
  }

  private reportAbandonment(pathname: string = window.location.pathname): void {
    // The marketing funnel has its own tracker; a venue-funnel-touching user
    // closing the tab on /marketing must not emit a bogus signup_abandoned.
    if (pathname.startsWith('/marketing')) return;
    if (this.abandonmentReported) return;
    if (localStorage.getItem(LocalStorageKey.FUNNEL_COMPLETED) === 'true') return;
    if (!localStorage.getItem(LocalStorageKey.FUNNEL_START_TIME)) return;

    this.abandonmentReported = true;

    const furthest = this.getFurthestStep();
    const furthestStep = Object.values(FUNNEL_STEPS).find(s => s.number === furthest);

    this.analytics.trackEvent('signup_abandoned', {
      last_step: this.currentStep?.name || 'unknown',
      last_step_number: this.currentStep?.number ?? 0,
      furthest_step: furthestStep?.name || 'unknown',
      furthest_step_number: furthest,
      time_on_last_step_ms: this.currentStep
        ? this.analytics.getTimeOnPage(this.currentStep.name)
        : 0,
      total_elapsed_ms: this.getFunnelElapsedMs(),
      ...this.attribution.getAttribution()
    });
  }

  private getFurthestStep(): number {
    return Number(localStorage.getItem(LocalStorageKey.FUNNEL_FURTHEST_STEP)) || 0;
  }

  private resolveStep(routeOrStepName: string): FunnelStep | null {
    if (FUNNEL_STEPS[routeOrStepName]) return FUNNEL_STEPS[routeOrStepName];
    return Object.values(FUNNEL_STEPS).find(s => s.name === routeOrStepName) || null;
  }
}
